import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Crown, Menu as MenuIcon, X, Phone } from "lucide-react";
import { SITE, scrollToId } from "../data/site";
import { OrderButton } from "./OrderDialog";
import { useLang, LangToggle } from "../i18n";

const SECTION_LINKS = [
    { key: "home", id: "home", testId: "nav-link-home" },
    { key: "story", id: "story", testId: "nav-link-story" },
    { key: "gallery", id: "gallery", testId: "nav-link-gallery" },
    { key: "reviews", id: "reviews", testId: "nav-link-reviews" },
    { key: "contact", id: "contact", testId: "nav-link-contact" },
];

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useLang();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const goSection = (id) => {
        setOpen(false);
        if (location.pathname !== "/") {
            navigate("/", { state: { scrollTo: id } });
        } else {
            scrollToId(id);
        }
    };

    const goMenu = () => {
        setOpen(false);
        navigate("/menu");
        window.__lenis?.scrollTo(0, { immediate: true });
    };

    return (
        <>
            <motion.header
                initial={{ y: -80, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
                className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
                    scrolled
                        ? "bg-ink/85 backdrop-blur-md border-b border-brass/15"
                        : "bg-transparent border-b border-transparent"
                }`}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-8 h-[72px] flex items-center justify-between">
                    <button
                        data-testid="nav-brand-logo"
                        onClick={() => goSection("home")}
                        className="flex items-center gap-3 group"
                        aria-label="Royal Tadka home"
                    >
                        <span className="w-9 h-9 rounded-full gold-border bg-elevated/70 flex items-center justify-center group-hover:border-saffron/60 transition-colors duration-300">
                            <Crown className="w-4 h-4 text-saffron-bright" strokeWidth={1.5} />
                        </span>
                        <span className="text-left leading-none">
                            <span className="block font-cinzel font-bold tracking-[0.18em] text-sm sm:text-base text-cream">
                                ROYAL TADKA
                            </span>
                            <span className="block text-[9px] tracking-[0.4em] text-brass/80 mt-1 uppercase">
                                Kraków
                            </span>
                        </span>
                    </button>

                    <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
                        {SECTION_LINKS.slice(0, 2).map((l) => (
                            <button
                                key={l.id}
                                data-testid={l.testId}
                                onClick={() => goSection(l.id)}
                                className="text-[13px] tracking-[0.14em] uppercase text-stone-300 hover:text-saffron-glow transition-colors duration-300"
                            >
                                {t(`nav.${l.key}`)}
                            </button>
                        ))}
                        <button
                            data-testid="nav-link-menu"
                            onClick={goMenu}
                            className={`text-[13px] tracking-[0.14em] uppercase transition-colors duration-300 ${
                                location.pathname === "/menu"
                                    ? "text-saffron-glow"
                                    : "text-stone-300 hover:text-saffron-glow"
                            }`}
                        >
                            {t("nav.menu")}
                        </button>
                        {SECTION_LINKS.slice(2).map((l) => (
                            <button
                                key={l.id}
                                data-testid={l.testId}
                                onClick={() => goSection(l.id)}
                                className="text-[13px] tracking-[0.14em] uppercase text-stone-300 hover:text-saffron-glow transition-colors duration-300"
                            >
                                {t(`nav.${l.key}`)}
                            </button>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        <LangToggle />
                        <OrderButton testId="nav-order-online-button" className="hidden sm:inline-flex px-6 py-2.5 text-xs">
                            {t("nav.order")}
                        </OrderButton>
                        <button
                            data-testid="nav-mobile-menu-toggle"
                            onClick={() => setOpen(true)}
                            className="lg:hidden w-11 h-11 rounded-full gold-border flex items-center justify-center text-cream"
                            aria-label="Open menu"
                        >
                            <MenuIcon className="w-5 h-5" strokeWidth={1.5} />
                        </button>
                    </div>
                </div>
            </motion.header>
            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-nav-drawer"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        className="fixed inset-0 z-[80] bg-ink/95 backdrop-blur-2xl flex flex-col"
                    >
                        <div className="flex items-center justify-between px-5 h-[72px]">
                            <span className="font-cinzel font-bold tracking-[0.18em] text-cream">ROYAL TADKA</span>
                            <button
                                data-testid="mobile-nav-close"
                                onClick={() => setOpen(false)}
                                className="w-11 h-11 rounded-full gold-border flex items-center justify-center"
                                aria-label="Close menu"
                            >
                                <X className="w-5 h-5" strokeWidth={1.5} />
                            </button>
                        </div>
                        <nav className="flex-1 flex flex-col justify-center px-8 gap-2" aria-label="Mobile">
                            {SECTION_LINKS.map((l, i) => (
                                <motion.button
                                    key={l.id}
                                    data-testid={`mobile-${l.testId}`}
                                    initial={{ opacity: 0, x: -24 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.08 + i * 0.06, duration: 0.5 }}
                                    onClick={() => goSection(l.id)}
                                    className="text-left font-serif text-4xl text-cream/90 hover:text-saffron-glow transition-colors duration-300 py-2"
                                >
                                    {t(`nav.${l.key}`)}
                                </motion.button>
                            ))}
                            <motion.button
                                data-testid="mobile-nav-link-menu"
                                initial={{ opacity: 0, x: -24 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.14 + SECTION_LINKS.length * 0.06, duration: 0.5 }}
                                onClick={goMenu}
                                className="text-left font-serif text-4xl italic gold-text py-2"
                            >
                                {t("nav.menu")}
                            </motion.button>
                        </nav>
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5, duration: 0.5 }}
                            className="px-8 pb-10 space-y-4"
                        >
                            <div className="flex justify-center">
                                <LangToggle />
                            </div>
                            <OrderButton testId="mobile-nav-order-button" className="w-full py-4 text-sm">
                                {t("nav.order")}
                            </OrderButton>
                            <a
                                data-testid="mobile-nav-call-link"
                                href={SITE.phoneHref}
                                className="flex items-center justify-center gap-2 text-sm text-stone-300"
                            >
                                <Phone className="w-4 h-4 text-saffron-bright" /> {SITE.phoneDisplay}
                            </a>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
